import { CellValueChangedEvent, Column } from "@ag-grid-community/core";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { get, set } from "lodash-es";
import { useCallback } from "react";
import {
  FieldValues,
  Resolver,
  useFormContext,
  UseFormReturn,
} from "react-hook-form";
import { FundFamilyUseAllocationTableGetErrorForCellFn } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { flattenFormFieldErrors } from "shared/utils/ErrorUtils";
import { useDeepCompareMemo } from "use-deep-compare";

export type FundFamilyUseAllocationTableResolver<T extends FieldValues> =
  Resolver<T> & {
    rowsPath: string | undefined;
  };

export const fundFamilyUseAllocationTableResolver = <T extends FieldValues>(
  wrappedResolver: Resolver<T>,
  rowsPath = "rows",
): Resolver<T> => {
  const resolver: FundFamilyUseAllocationTableResolver<T> = async (
    values,
    context,
    options,
  ) => {
    const result = await wrappedResolver(values, context, options);

    const rows = get(values, rowsPath);
    const rowErrors = get(result.errors, rowsPath);

    if (rows && rowErrors && Array.isArray(rowErrors)) {
      const rowErrorsById = rowErrors.reduce((acc, rowError, rowIndex) => {
        const rowId = (
          rows[rowIndex] as FundFamilyUseAllocationTableRow | undefined
        )?.id;
        if (!rowId) {
          return acc;
        }
        return {
          ...acc,
          [rowId]: rowError,
        };
      }, {});
      set(result.errors, rowsPath, rowErrorsById);
    }

    return result;
  };
  resolver.rowsPath = rowsPath;
  return resolver;
};

export const useFundFamilyUseAllocationTableFormOnCellValueChanged = () => {
  const form = useFormContext() as UseFormReturn | undefined;
  const formErrors = form?.formState.errors;
  const formResolver = form?.control._options.resolver;
  return useCallback(
    (event: CellValueChangedEvent) => {
      const rowId = event.node.id!;
      if (!formErrors || !formResolver || !rowId) {
        return undefined;
      }
      const rowsPath = getRowsPathFromFormResolver(formResolver);
      form.clearErrors(`${rowsPath}.${rowId}`);
    },
    [form, formErrors, formResolver],
  );
};

export const useFundFamilyUseAllocationTableGetErrorForCellFnFromForm = () => {
  const form = useFormContext() as UseFormReturn | undefined;
  const formErrors = useDeepCompareMemo(
    () => form?.formState.errors,
    [form?.formState.errors],
  );

  const formResolver = form?.control._options.resolver;
  return useCallback<FundFamilyUseAllocationTableGetErrorForCellFn>(
    ({ column, node }) => {
      if (!formErrors || !formResolver) {
        return undefined;
      }
      const rowsPath = getRowsPathFromFormResolver(formResolver);
      const rowId = node.data?.id;
      if (!(column instanceof Column) || !rowId) {
        return undefined;
      }
      const colFieldOrId = column.getColDef().field || column.getColId();
      const path = `${rowsPath}.${rowId}.${colFieldOrId}`;
      return flattenFormFieldErrors(formErrors)
        .filter(
          ({ fieldPath }) =>
            fieldPath === path || fieldPath.startsWith(`${path}.`),
        )
        .map(({ error }) => error)
        .join(", ");
    },
    [formResolver, formErrors],
  );
};

const getRowsPathFromFormResolver = (formResolver: Resolver) => {
  const resolver =
    formResolver as FundFamilyUseAllocationTableResolver<FieldValues>;
  if (!resolver.rowsPath) {
    throw new Error(
      "form resolver must be wrapped with fundFamilyUseAllocationTableResolver",
    );
  }
  return resolver.rowsPath;
};
